import React from 'react';
import { useNavigate } from 'react-router-dom';
import SignalBadge from './SignalBadge';
import StrategyMatchBar from './StrategyMatchBar';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import type { SignalType, SignalStrength } from '../services/api';
import type { StrategyMatchResult } from '../types/signals';

const isStreamingSymbol = (symbol: string) =>
  symbol.endsWith('.FOREX') || symbol.endsWith('.CC');

interface DashboardSignalCardProps {
  symbol: string;
  price: number;
  change_percent: number;
  previousClose?: number;
  signal: SignalType;
  strength: SignalStrength;
  confidence: number;
  strategy_breakdown?: StrategyMatchResult[];
  match_score?: number;
  strategies_evaluated?: number;
  strategies_matched?: number;
  convergence_count?: number;
  convergence_boost?: number;
  showSignals?: boolean;
}

function borderColour(signal: SignalType): string {
  if (signal === 'buy' || signal === 'strong_buy') return 'border-emerald-500/30';
  if (signal === 'sell' || signal === 'strong_sell') return 'border-red-500/30';
  return 'border-amber-500/30';
}

const DashboardSignalCard: React.FC<DashboardSignalCardProps> = ({
  symbol,
  price,
  change_percent,
  previousClose,
  signal,
  strength,
  confidence,
  strategy_breakdown,
  match_score,
  strategies_evaluated,
  strategies_matched,
  convergence_count,
  convergence_boost,
  showSignals = true,
}) => {
  const navigate = useNavigate();
  const isStreaming = symbol.endsWith('.FOREX') || symbol.endsWith('.CC');
  const livePrice = useRealtimePrice(isStreaming ? symbol : '');

  const isJSE = symbol.endsWith('.JSE');
  const currency = isJSE ? 'R' : '$';

  const displayPrice = livePrice.isLive && livePrice.price ? livePrice.price : price;
  const displayChange =
    livePrice.isLive && livePrice.price && previousClose
      ? ((livePrice.price - previousClose) / previousClose) * 100
      : change_percent;

  const bestMatch =
    strategy_breakdown?.find((r) => r.is_best_match) ?? strategy_breakdown?.[0];

  const firedChips = (bestMatch?.patterns_fired ?? []).map((p) => ({ label: p, fired: true }));
  const missedChips = (bestMatch?.patterns_missed ?? []).map((p) => ({ label: p, fired: false }));
  const allChips = [...firedChips, ...missedChips].slice(0, 3);

  const handleNavigate = () =>
    navigate(`/asset/${symbol}`, { state: { strategy_breakdown } });

  return (
    <div
      onClick={handleNavigate}
      className={`p-3 rounded-lg border bg-slate-800/50 cursor-pointer hover:bg-slate-700/40 transition-colors space-y-2 ${borderColour(signal)}`}
    >
      {/* Header row: symbol + price + badge */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{symbol}</span>
            {livePrice.isLive && isStreamingSymbol(symbol) && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
                <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                LIVE
              </span>
            )}
        </div>
        <p className="text-xs text-slate-500">
            {currency} {displayPrice.toLocaleString()}
            <span className={displayChange >= 0 ? 'text-emerald-400' : 'text-red-400'}>
              {' '}
              {displayChange >= 0 ? '+' : ''}
              {displayChange.toFixed(2)}%
            </span>
          </p>
        </div>
        {showSignals ? (
          <SignalBadge signal={signal} strength={strength} confidence={confidence} size="sm" />
        ) : (
          <span className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-slate-700/50 text-slate-300">
            Signal hidden
          </span>
        )}
      </div>

      {/* Strategy match bar */}
      {bestMatch && (
        <StrategyMatchBar
          matchScore={match_score ?? 0}
          strategyName={bestMatch.strategy_name}
          strategiesMatched={strategies_matched ?? 0}
          strategiesEvaluated={strategies_evaluated ?? 0}
        />
      )}

      {/* Pattern chips (top-3) */}
      {allChips.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="pattern-chips">
          {allChips.map((chip, idx) => (
            <span
              key={idx}
              className={
                chip.fired
                  ? 'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
                  : 'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-slate-700/60 text-slate-500 ring-1 ring-slate-600/50'
              }
              data-testid={chip.fired ? 'chip-fired' : 'chip-missed'}
            >
              {chip.fired ? '✓' : '✗'} {chip.label}
            </span>
          ))}
        </div>
      )}

      {/* Convergence badge */}
      {(convergence_count ?? 0) >= 2 && (
        <div
          className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300"
          data-testid="convergence-badge"
        >
          🔗 {convergence_count} strategies converge on {bestMatch?.direction ?? '—'} (+
          {convergence_boost ?? 0}pt boost)
        </div>
      )}

      {/* CTA */}
      <div className="flex justify-end">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNavigate();
          }}
          className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
          data-testid="view-chart-cta"
        >
          View Chart &amp; Full Analysis →
        </button>
      </div>
    </div>
  );
};

export default DashboardSignalCard;
